import config from '../config/environment';
import type {
  LeadQualificationBusyRoutineBehavior,
  LeadQualificationConsultingExpectation,
  LeadQualificationInjuryLimitation,
  LeadQualificationInvestmentInterest,
  LeadQualificationMainGoal,
  LeadQualificationStartTimeExpectation,
  LeadQualificationTrainingExperience,
  LeadQualificationTrainingFormat,
  LeadQualificationTrainingRelationship,
  LeadQualificationWeeklyCommitmentDays,
} from '../pages/Checkout/checkout.types';

interface LeadQualificationSubmitParams {
  fullName: string;
  email: string;
  phone: string;
  callingCode: string;
  investmentInterest: LeadQualificationInvestmentInterest;
  startTimeExpectation: LeadQualificationStartTimeExpectation;
  mainGoal: LeadQualificationMainGoal;
  trainingRelationship: LeadQualificationTrainingRelationship;
  trainingExperience: LeadQualificationTrainingExperience;
  busyRoutineBehavior: LeadQualificationBusyRoutineBehavior;
  weeklyCommitmentDays: LeadQualificationWeeklyCommitmentDays;
  trainingFormat: LeadQualificationTrainingFormat;
  consultingExpectation: LeadQualificationConsultingExpectation;
  injuryLimitation: LeadQualificationInjuryLimitation;
}

interface LeadQualificationResponse {
  qualification_id: number;
  /** @deprecated Prefer qualification_id; retained for legacy responses. */
  student_id?: number | null;
}

interface StudentsFunctionResponse {
  success?: boolean;
  data?: {
    qualification_id?: number;
    qualificationId?: number;
    student_id?: number | null;
    studentId?: number | null;
  };
  error?: {
    message?: string;
  } | string;
}

const resolveErrorMessage = (body: StudentsFunctionResponse): string => {
  if (typeof body.error === 'string') {
    return body.error;
  }

  return body.error?.message ?? 'Não foi possível enviar sua qualificação.';
};

class LeadQualificationServiceClass {
  async submit(params: LeadQualificationSubmitParams): Promise<LeadQualificationResponse> {
    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${config.supabaseAnonKey}`,
      },
      body: JSON.stringify({
        action: 'qualify-lead',
        payload: {
          personal: {
            fullName: params.fullName,
            email: params.email,
            callingCode: params.callingCode,
            phone: params.phone,
          },
          investmentInterest: params.investmentInterest,
          startTimeExpectation: params.startTimeExpectation,
          mainGoal: params.mainGoal,
          trainingRelationship: params.trainingRelationship,
          trainingExperience: params.trainingExperience,
          busyRoutineBehavior: params.busyRoutineBehavior,
          weeklyCommitmentDays: params.weeklyCommitmentDays,
          trainingFormat: params.trainingFormat,
          consultingExpectation: params.consultingExpectation,
          injuryLimitation: params.injuryLimitation,
        },
      }),
    });

    const body = await response.json().catch(() => ({})) as StudentsFunctionResponse;

    if (!response.ok || body.success === false || !body.data) {
      throw new Error(resolveErrorMessage(body));
    }

    const qualificationId = Number(body.data.qualification_id ?? body.data.qualificationId);
    if (!Number.isInteger(qualificationId) || qualificationId <= 0) {
      throw new Error('Resposta inválida: qualification_id ausente.');
    }

    const rawStudentId = body.data.student_id ?? body.data.studentId;
    const studentId = rawStudentId == null ? undefined : Number(rawStudentId);

    return {
      qualification_id: qualificationId,
      ...(studentId != null && Number.isInteger(studentId) && studentId > 0
        ? { student_id: studentId }
        : {}),
    };
  }
}

export const LeadQualificationService = new LeadQualificationServiceClass();
